"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { StreamingResponse } from "@/app/lib/types";
import MarkdownRenderer from "./MarkdownRenderer";
import StreamingIndicator from "./StreamingIndicator";
import { formatDuration } from "@/app/lib/format";
import { buildResponseCardCopyText, shouldShowResponseRetry } from "@/app/lib/responseCardText";

interface ResponseCardProps {
  providerId: string;
  providerName: string;
  providerColor: string;
  providerIcon?: string;
  response: StreamingResponse;
  isStreaming: boolean;
  mode?: string;
  // Per-provider retry handler (OQ-S6-a). When iPhone's interrupted-or-
  // terminal-error rule applies, a Retry button is rendered. Caller is
  // responsible for resume-then-rerun plumbing; this component only drives
  // the spinner state and the click.
  onRetry?: (providerId: string) => Promise<void>;
  // Desktop-web tap-to-expand: when provided, the header becomes clickable
  // and fires this. Parent renders the CardExpandPopup with the same
  // ResponseCard inside (sized via `inPopup` below).
  onExpand?: () => void;
  // When true, the body uses a generous popup-height (no viewport calc) so
  // the user sees the full response without the card-grid clipping. Used
  // by the expand-popup wrapper.
  inPopup?: boolean;
  // TTS read-aloud props (optional — when omitted the button is hidden).
  // Active when this (recordId, providerId) is currently the TTS target.
  recordId?: string;
  onReadAloud?: (recordId: string, providerId: string, text: string) => void;
  isTTSActive?: boolean;
  ttsProgress?: number; // 0..1 — only meaningful when isTTSActive
  // True when the per-user TTS cache already holds this card's audio.
  // Drives the play-button icon (matches iOS): SOLID filled play triangle
  // = cached (free + instant), HOLLOW/outline play triangle = uncached
  // (will cost an OpenAI synth on tap).
  // Undefined = not yet probed → render hollow (conservative default).
  isTTSCached?: boolean;
}

const FULL_DISPLAY_KEYS: Record<string, string> = {
  reasoning_effort: "reasoning",
  thinking_level: "thinking",
  max_output_tokens: "max tokens",
  max_tokens: "max tokens",
  thinking_budget: "thinking budget",
  budget_tokens: "thinking budget",
  max_web_searches: "web searches",
  max_tool_calls: "tool calls",
};

const SHORT_DISPLAY_KEYS: Record<string, string> = {
  reasoning_effort: "reasoning",
  thinking_level: "thinking",
  max_output_tokens: "max",
  max_tokens: "max",
  thinking_budget: "budget",
  budget_tokens: "budget",
  max_web_searches: "search",
  max_tool_calls: "tools",
};

function ProviderIcon({ icon, color }: { icon?: string; color: string }) {
  const size = 14;
  const style = { flexShrink: 0 } as const;
  if (icon === "sparkles") {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill={color} style={style}>
        <path d="M8 0l1.5 5.5L15 7l-5.5 1.5L8 14l-1.5-5.5L1 7l5.5-1.5L8 0z" />
        <path d="M12.5 1l.75 2.25L15.5 4l-2.25.75L12.5 7l-.75-2.25L9.5 4l2.25-.75L12.5 1z" opacity="0.6" />
      </svg>
    );
  }
  if (icon === "brain.head.profile") {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill={color} style={style}>
        <path d="M10 2.5a3.5 3.5 0 00-3.08 1.84A3 3 0 004 7a3 3 0 00.26 1.23A2.5 2.5 0 005 13h4.5a3 3 0 002.86-2.07A2.5 2.5 0 0013 8.5 2.5 2.5 0 0012.5 6a2.5 2.5 0 00-.09-.5A3.5 3.5 0 0010 2.5z" />
        <circle cx="8.5" cy="7" r="0.75" fill="#161b22" />
        <path d="M7 9.5c.5.5 1.5.5 2 0" stroke="#161b22" strokeWidth="0.75" fill="none" strokeLinecap="round" />
      </svg>
    );
  }
  // Default: text.bubble (Anthropic)
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill={color} style={style}>
      <path d="M2 3a2 2 0 012-2h8a2 2 0 012 2v6a2 2 0 01-2 2H6.5L3 14V11a2 2 0 01-2-2V3z" />
      <path d="M5.5 5h5M5.5 7.5h3" stroke="#161b22" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

export default function ResponseCard({
  providerId,
  providerName,
  providerColor,
  providerIcon,
  response,
  isStreaming,
  mode: modeProp,
  onRetry,
  onExpand,
  inPopup,
  recordId,
  onReadAloud,
  isTTSActive,
  ttsProgress,
  isTTSCached,
}: ResponseCardProps) {
  const [copied, setCopied] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const metaRef = useRef<HTMLDivElement>(null);
  // Adaptive meta levels:
  //   0 = full labels for all parameters
  //   1 = short labels for all parameters
  //   2 = short labels without web searches or tool calls
  //   3 = short labels without token, web-search, or tool-call parameters
  // By owner decision, unlike JudgeCard, the response model is retained at
  // every level: it is this card's identity and must not be dropped.
  const [metaLevel, setMetaLevel] = useState<0 | 1 | 2 | 3>(0);
  const lastContentKeyRef = useRef("");

  const handleReadAloud = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!onReadAloud || !recordId || !response.text) return;
    onReadAloud(recordId, providerId, response.text);
  }, [onReadAloud, recordId, providerId, response.text]);
  // INVARIANT: spinner and copy button are mutually exclusive in the
  // top-right corner. They render at the same visual location — the
  // spinner is in Row 1's flex flow, the copy button is absolutely
  // positioned at top:4/right:7 — and overlap if both render. Gate
  // them on the SAME `isLoading` expression (spinner: isLoading; copy:
  // !isLoading) so only one is ever visible.
  const isLoading = isStreaming && !response.isComplete;

  // Play button appears only once the output has COMPLETED (not mid-
  // stream): a loading card shows the spinner alone. Gating on
  // `!isLoading` keeps the read-aloud control out of the corner until
  // the copy button takes its place on completion.
  const showReadAloud = !!onReadAloud && !!recordId && !!response.text && !isLoading;

  const handleRetry = useCallback(async () => {
    if (!onRetry || isRetrying) return;
    setIsRetrying(true);
    try {
      await onRetry(providerId);
    } catch (err) {
      console.error("Retry failed:", err);
    } finally {
      setIsRetrying(false);
    }
  }, [onRetry, providerId, isRetrying]);

  // Flatten options for display. Server receives options in a mix of
  // shapes: flat snake_case (Anthropic, OpenAI), nested generationConfig
  // for Gemini, and renamed keys (thinking_budget → budget_tokens for
  // Anthropic). Normalize to a list of flat [key, value] pairs.

  const flatOptions: Record<string, unknown> = {};
  if (response.options) {
    for (const [key, value] of Object.entries(response.options)) {
      if (key === "generationConfig" && value && typeof value === "object") {
        // Gemini nested shape — unpack camelCase → snake_case for matching.
        for (const [nk, nv] of Object.entries(value as Record<string, unknown>)) {
          if (nk === "maxOutputTokens") flatOptions.max_output_tokens = nv;
          else if (nk === "thinkingLevel") flatOptions.thinking_level = nv;
        }
      } else {
        flatOptions[key] = value;
      }
    }
  }

  const optionEntries = Object.entries(flatOptions).filter(
    ([key]) => FULL_DISPLAY_KEYS[key] !== undefined
  );

  const mode = modeProp || "";
  const modeLabel = mode === "deep_research" ? "Deep Research" : mode ? mode.charAt(0).toUpperCase() + mode.slice(1) : "";

  const durationText = response.isComplete && response.durationMs != null ? formatDuration(response.durationMs) : "";

  // Deliberately excludes response text and streaming/completion state: this
  // card re-renders per token, but only actual meta-content changes should
  // restart the abbreviation ladder.
  const contentKey = JSON.stringify([response.model ?? "", response.meta ?? "", optionEntries]);

  // Start each content change from the most informative form, then measure
  // the real two-line clamp and progressively abbreviate only if necessary.
  useLayoutEffect(() => {
    const el = metaRef.current;
    if (!el) return;
    if (lastContentKeyRef.current !== contentKey) {
      lastContentKeyRef.current = contentKey;
      if (metaLevel !== 0) {
        setMetaLevel(0);
        return;
      }
    }
    if (metaLevel < 3 && el.scrollHeight > el.clientHeight + 1) {
      setMetaLevel((metaLevel + 1) as 0 | 1 | 2 | 3);
    }
  });

  // A wider card gets another chance to use full labels. Track width so
  // content-driven height changes do not unnecessarily restart the ladder.
  useEffect(() => {
    const el = metaRef.current;
    if (!el) return;
    let lastWidth = el.clientWidth;
    const observer = new ResizeObserver(() => {
      const width = el.clientWidth;
      if (width !== lastWidth) {
        lastWidth = width;
        setMetaLevel(0);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const buildInfoParts = (level: 0 | 1 | 2 | 3): string[] => {
    const labels = level === 0 ? FULL_DISPLAY_KEYS : SHORT_DISPLAY_KEYS;
    const parts: string[] = [];
    // Owner decision: always retain the response model, unlike JudgeCard.
    if (response.model) parts.push(response.model);
    if (response.meta) {
      parts.push(response.meta);
    } else {
      optionEntries.forEach(([key, value]) => {
        if (level >= 2 && (key === "max_web_searches" || key === "max_tool_calls")) return;
        if (level >= 3 && (key === "max_output_tokens" || key === "max_tokens")) return;
        parts.push(`${labels[key]}: ${String(value)}`);
      });
    }
    return parts;
  };
  const infoParts = buildInfoParts(metaLevel);
  // Keep clipboard metadata in lockstep with the two pieces of context the
  // card visibly renders: mode beside the provider name, then model/options
  // on the card's metadata row.
  const copyMetaLine1 = [modeLabel, infoParts.join(" | ")].filter(Boolean).join(" | ");
  const handleCopy = useCallback((e?: React.MouseEvent) => {
    // Don't bubble into the header onClick (expand-popup trigger).
    e?.stopPropagation();
    navigator.clipboard.writeText(buildResponseCardCopyText({
      providerName,
      metaLine1: copyMetaLine1,
      metaLine2: durationText,
      text: response.text,
    })).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [providerName, copyMetaLine1, durationText, response.text]);
  // Mirrors iPhone's CompactChatView.shouldShowRetry: interrupted responses
  // are retryable even while their partial text is still visible.
  const showRetry = shouldShowResponseRetry({
    hasRetryHandler: !!onRetry,
    wasInterrupted: response.wasInterrupted,
    hasError: !!response.error,
    isComplete: response.isComplete,
  });

  const retryButton = showRetry ? (
    <button
      onClick={handleRetry}
      disabled={isRetrying}
      className="btn-ghost"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 10px",
        fontSize: "12px",
        fontWeight: 500,
        color: providerColor,
        border: `1px solid color-mix(in srgb, ${providerColor} 40%, transparent)`,
        borderRadius: "6px",
        background: "transparent",
        cursor: isRetrying ? "default" : "pointer",
        opacity: isRetrying ? 0.6 : 1,
      }}
      title="Retry this provider"
    >
      {isRetrying ? (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ animation: "spin 1.2s linear infinite" }}>
          <circle cx="8" cy="8" r="6.5" stroke={providerColor} strokeWidth="1.5" opacity="0.25" />
          <path d="M14.5 8a6.5 6.5 0 00-6.5-6.5" stroke={providerColor} strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 8a6 6 0 1 1 2 4.5" />
          <path d="M2 13V9h4" />
        </svg>
      )}
      {isRetrying ? "Retrying…" : "Retry"}
    </button>
  ) : null;
  return (
    <div
      className="response-card"
      style={{
        border: isTTSActive
          ? `1px solid ${providerColor}`
          : `1px solid color-mix(in srgb, ${providerColor} 30%, transparent)`,
        boxShadow: isTTSActive
          ? `0 0 0 1px color-mix(in srgb, ${providerColor} 50%, transparent)`
          : undefined,
        position: "relative",
        background: "#161b22",
        padding: 0,
        // In the popup: fill the card-expand-popup-body container (flex:1
        // there gives us our height). Become a flex column so the body
        // can flex:1 + overflow:auto while header stays fixed at top.
        ...(inPopup
          ? {
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column" as const,
            }
          : {}),
      }}
    >
      {/* Header block — locked at 64px so the response card matches JudgeCard
          height exactly in the 4-up grid/pager. Padding reduced 50% (8/14 →
          4/7) to give Row 2 (model+params) more vertical room before
          truncation and Row 1 more horizontal room (4-up shrinks card width). */}
      <div
        onClick={onExpand && !inPopup ? onExpand : undefined}
        data-card-header={inPopup ? "true" : undefined}
        style={{
          background: "#1c2129",
          borderRadius: "var(--radius-lg) var(--radius-lg) 0 0",
          padding: "4px 7px",
          height: "64px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: "2px",
          overflow: "hidden",
          cursor: onExpand && !inPopup ? "pointer" : "default",
          // position: relative so the absolute-positioned copy/play
          // button column (below) anchors to this header box — and
          // therefore can't extend Row 1's natural height or squeeze
          // Row 2 (the meta line).
          position: "relative",
          // Header sticky behavior in the popup is implemented via CSS
          // rules in globals.css keyed on .card-expand-popup-body.is-stuck
          // — see CardExpandPopup. We tried position:sticky here and it
          // failed in Safari; JS-driven absolute positioning is reliable.
        }}
      >
        {/* Row 1: icon + provider name + mode label + spacer + duration / spinner.
            Buttons (copy + play) are NOT in Row 1's flex — they live in an
            absolute-positioned column further down (after Row 2) so their
            stacked height can't expand Row 1 and squeeze Row 2's 2-line clamp. */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexShrink: 0,
          // Reserve horizontal space for the absolute-positioned button
          // column so the duration text (when shown) doesn't slide under it.
          paddingRight: response.text ? 22 : 0,
        }}>
          <ProviderIcon icon={providerIcon} color={providerColor} />
          <span style={{ fontWeight: 600, color: providerColor, fontSize: "13px" }}>{providerName}</span>
          {modeLabel && <span style={{ fontSize: "11px", color: "var(--text-tertiary)", fontWeight: 400 }}>{modeLabel}</span>}
          <span style={{ flex: 1 }} />
          {durationText && <span style={{ fontSize: "12px", color: "var(--text-tertiary)" }}>{durationText}</span>}
          {isLoading && (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ animation: "spin 1.2s linear infinite" }}>
              <circle cx="8" cy="8" r="6.5" stroke={providerColor} strokeWidth="1.5" opacity="0.25" />
              <path d="M14.5 8a6.5 6.5 0 00-6.5-6.5" stroke={providerColor} strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          )}
        </div>
        {/* Row 2: model + params. 10px monospaced matches iOS JudgeCard meta
            font (META_FONT_SIZE) so two cards align visually. At 12px the
            params row was clipping at the bottom because the locked 64px
            header doesn't have room for two 16.8px-tall lines plus Row 1
            plus padding. Color matches JudgeCard meta (var(--text-secondary))
            so all four cards in the 4-up grid have the same meta-row shade. */}
        <div ref={metaRef} style={{
          fontSize: "10px",
          color: "var(--text-secondary)",
          lineHeight: 1.4,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
          flex: 1,
          minHeight: 0,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical" as const,
          overflow: "hidden",
          textOverflow: "ellipsis",
          // Reserve horizontal space for the absolute-positioned button
          // column so the meta line doesn't slide under it.
          paddingRight: response.text ? 22 : 0,
        }}>
          {infoParts.map((part, i) => (
            <span key={i}>
              {part}{i < infoParts.length - 1 ? <span style={{ whiteSpace: "nowrap" }}>&nbsp;|</span> : null}
              {i < infoParts.length - 1 ? " " : ""}
            </span>
          ))}
        </div>

        {/* Absolute-positioned copy + play column. Anchored to the
            header (which has position:relative). Sits OUTSIDE Row 1
            and Row 2's flex flow so it cannot push them around — the
            header's locked 64px stays 64px, Row 2's 2-line meta clamp
            keeps its full vertical budget. Stop-propagation on click
            so taps don't bubble into the header's onExpand. */}
        {response.text && (
          <div style={{
            position: "absolute",
            top: 4,
            right: 7,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 2,
          }}>
            {!isLoading && (
              <button onClick={(e) => handleCopy(e)} className="btn-ghost" style={{ padding: "2px", lineHeight: 0, opacity: 0.75 }} title="Copy response">
                {copied ? (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13.25 4.75l-6.5 6.5-3.5-3.5" /></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="5.5" y="5.5" width="8" height="8" rx="1.5" /><path d="M10.5 5.5V3.5a1.5 1.5 0 00-1.5-1.5H3.5A1.5 1.5 0 002 3.5V9a1.5 1.5 0 001.5 1.5h2" /></svg>
                )}
              </button>
            )}
            {showReadAloud && (
              <button
                onClick={(e) => handleReadAloud(e)}
                className="btn-ghost"
                style={{
                  padding: "2px",
                  lineHeight: 0,
                  opacity: isTTSActive ? 1 : 0.5,
                  color: isTTSActive ? providerColor : undefined,
                }}
                title={isTTSActive ? "Playing" : isTTSCached ? "Play (cached)" : "Play (will synthesize)"}
                aria-label={isTTSActive ? "Currently playing" : "Read this response aloud"}
              >
                {isTTSActive ? (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <rect x="3" y="6" width="2" height="4" rx="0.5" />
                    <rect x="7" y="3" width="2" height="10" rx="0.5" />
                    <rect x="11" y="5" width="2" height="6" rx="0.5" />
                  </svg>
                ) : isTTSCached ? (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M5 3.5l7 4.5-7 4.5V3.5z" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 3.5l7 4.5-7 4.5V3.5z" />
                  </svg>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="response-card-body" style={{
        fontSize: "14px",
        lineHeight: 1.6,
        padding: "12px 7px",
        // In the popup: this is the ONLY scroll surface in the modal.
        // The card outer is a flex column; this body region flexes to
        // fill remaining height after the fixed 64px header above.
        ...(inPopup
          ? {
              flex: 1,
              minHeight: 0,
              overflowY: "auto" as const,
              overflowX: "auto" as const,
            }
          : {
              // SINGLE viewport-anchored height for every card state.
              // Subtracting `--query-area-h` made streaming cards shrink
              // when the user's prompt was long (screenshot 2026-05-15:
              // multi-paragraph prompt → cards visibly tiny) AND made
              // each card's height flip the moment its provider's
              // isComplete toggled, so a row mid-stream had unequal
              // heights (screenshot 2026-05-15: Anthropic shorter than
              // Gemini/OpenAI). The full-screen-as-if-no-prompt height
              // is the authoritative target — page scrolls vertically
              // when the prompt area pushes content past the viewport.
              //
              // INVARIANT: this formula MUST match JudgeCard.tsx exactly.
              // Edit both files together. No `--query-area-h` subtraction.
              // No `isComplete` / streaming-state branching. See
              // design/card_heights.md before editing.
              height: "calc(100dvh - var(--bottom-bar-h, 120px) - var(--attachment-bar-h, 0px) - 160px)",
              overflowY: "auto" as const,
              overflowX: "auto" as const,
            }),
        overscrollBehavior: "auto",
        touchAction: "pan-y",
      }}>
        {response.narrationStatus && (
          <div
            style={{
              color: "var(--text-secondary)",
              fontSize: "12px",
              lineHeight: 1.4,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              marginBottom: "8px",
            }}
            title={response.narrationStatus}
          >
            {response.narrationStatus}
          </div>
        )}
        {isStreaming && !response.text && !response.error ? (
          <StreamingIndicator
            providerName={providerName}
            color={providerColor}
          />
        ) : isRetrying && !response.text ? (
          <StreamingIndicator
            providerName={providerName}
            color={providerColor}
          />
        ) : response.text ? (
          <>
            <MarkdownRenderer content={response.text} isStreamComplete={response.isComplete} inPopup={inPopup} />
            {showRetry && (
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px", marginTop: "12px" }}>
                {response.wasInterrupted && (
                  <span style={{ color: "#f0a020", fontSize: "12px" }}>Response may be incomplete</span>
                )}
                {retryButton}
              </div>
            )}
          </>
        ) : response.error ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "flex-start" }}>
            <div style={{ color: "var(--error)" }}>{response.error}</div>
            {retryButton}
          </div>
        ) : null}
      </div>

      {/* TTS playback progress bar — only when this card is the active
          TTS target. Absolute-positioned at the very bottom of the
          outer card so it overlays without affecting layout. */}
      {isTTSActive && typeof ttsProgress === "number" && (
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 2,
            background: "color-mix(in srgb, " + providerColor + " 20%, transparent)",
            pointerEvents: "none",
            borderRadius: "0 0 var(--radius-lg) var(--radius-lg)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.min(100, Math.max(0, ttsProgress * 100))}%`,
              background: providerColor,
              transition: "width 120ms linear",
            }}
          />
        </div>
      )}
    </div>
  );
}
